import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useObjectiveQuery } from '../../../../__generated__/graphql';
import {
  mapEmployeeOption,
  mapRelatedGoal,
  toObjectiveIdInput,
} from '../mappings';
import { GoalId } from '../types';
import { useGoalUpdateSelectOptions } from '../useGoalSelectOptions';
import { GoalCommonFieldsSkeleton } from './GoalCommonFieldsSkeleton';
import { GoalSidebarMode } from './types';
import { mapToObjectiveFormData } from './mappings';
import { mapActivities } from '../GoalsActivities/mappings';
import { GoalUpdateForm } from './GoalUpdateForm';
import { mapGoalHierarchy } from './GoalHierarchy/mappings';

type Props = {
  goalId: GoalId;
  mode: GoalSidebarMode;
  onClose: () => void;
};

export const GoalSidebarUpdateContent: React.FC<
  React.PropsWithChildren<Props>
> = ({ goalId, mode, onClose }) => {
  const { t } = useTranslation();

  const { data, error, loading } = useObjectiveQuery({
    variables: {
      objectiveId: toObjectiveIdInput(goalId),
    },
  });

  const {
    options,
    error: optionsError,
    loading: optionsLoading,
  } = useGoalUpdateSelectOptions(
    data?.objective?.flair__Objective_Time_Period__c ?? undefined,
  );

  const isLoading = optionsLoading || loading || !data || !options;

  const objective = data?.objective ?? null;
  const formData = useMemo(
    () => (objective ? mapToObjectiveFormData(objective) : null),
    [objective],
  );

  if (error || optionsError) {
    return <div>{t(`performanceReview.goals2.errorLoading`)}</div>;
  }

  if (isLoading) {
    return <GoalCommonFieldsSkeleton />;
  }
  if (!objective || !formData) {
    return <div>{t(`performanceReview.goals2.goalNotFound`)}</div>;
  }

  const goalWithHierarchy = mapGoalHierarchy(objective);
  const alignedTo =
    objective.__typename === 'EmployeeObjective'
      ? objective.alignedTo.map(mapRelatedGoal)
      : [];

  const owner = objective.owner
    ? mapEmployeeOption(objective.owner)
    : undefined;

  const activities = mapActivities(objective, options);

  //This is done to handle the case when some goals have already inactive periods
  const periodsToShow = (options?.allPeriods ?? []).filter((p) => {
    if (p.active) {
      return true;
    } else {
      return formData.timePeriodId === p.value;
    }
  });

  const useOkr =
    objective.__typename === 'EmployeeObjective'
      ? objective.flair__Use_OKR__c ?? false
      : false;

  return (
    <GoalUpdateForm
      goalId={goalId}
      activities={activities}
      mode={mode}
      owner={owner}
      formData={formData}
      goalWithHierarchy={goalWithHierarchy}
      onClose={onClose}
      options={{ ...options, periods: periodsToShow }}
      alignedTo={alignedTo}
      useOkr={useOkr}
    />
  );
};
